import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/services/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setError('No reset token provided');
      setValidating(false);
    }
  }, [token]);

  const validateToken = async () => {
    try {
      await api.get(`/invites/reset-password/validate/${token}`);
      setValid(true);
    } catch (err) {
      setError('Invalid or expired reset link');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await api.post('/invites/reset-password/confirm', {
        token,
        newPassword,
      });
      alert('Password reset successful! You can now login with your new password.');
      navigate('/login');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-background dark:bg-brand-background">
        <div className="text-center">
          <div className="text-xl font-semibold text-brand-on-surface dark:text-brand-on-surface-variant">
            Validating reset link...
          </div>
        </div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-background dark:bg-brand-background p-4">
        <div className="max-w-md w-full bg-brand-surface dark:bg-brand-surface rounded-lg shadow-xl p-8 text-center">
          <div className="text-brand-error dark:text-brand-error text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-brand-on-background dark:text-brand-on-primary mb-4">
            Invalid Reset Link
          </h1>
          <p className="text-brand-on-surface-variant dark:text-brand-on-surface-variant mb-6">
            {error}
          </p>
          <button onClick={() => navigate('/login')} className="btn btn-primary w-full">
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-background dark:bg-brand-background p-4">
      <div className="max-w-md w-full bg-brand-surface dark:bg-brand-surface rounded-lg shadow-xl p-8">
        <h1 className="text-3xl font-bold text-brand-on-background dark:text-brand-on-primary mb-2">
          Reset Password
        </h1>
        <p className="text-brand-on-surface-variant dark:text-brand-on-surface-variant mb-6">
          Enter your new password below.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-2">
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 8 characters)"
              className="input w-full"
              required
              minLength={8}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className="input w-full"
              required
            />
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-brand-error dark:text-brand-error">{error}</p>
            </div>
          )}

          <button type="submit" disabled={loading} className="btn btn-primary w-full">
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>

          <button
            type="button"
            onClick={() => navigate('/login')}
            className="btn btn-secondary w-full"
          >
            Back to Login
          </button>
        </form>
      </div>
    </div>
  );
}
